import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const requestService = {
  // Request Management
  getRequests: async (params = {}) => {
    try {
      const response = await api.get('/request-system', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getRequest: async (id) => {
    try {
      const response = await api.get(`/request-system/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createRequest: async (requestData) => {
    try {
      const formData = new FormData();
      
      // Add request data
      Object.keys(requestData).forEach(key => {
        if (key !== 'attachments') {
          formData.append(key, requestData[key]);
        }
      });

      // Add attachments if any
      if (requestData.attachments) {
        requestData.attachments.forEach(file => {
          formData.append('attachments', file);
        });
      }

      const response = await api.post('/request-system', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateRequest: async (id, requestData) => {
    try {
      const formData = new FormData();
      
      // Add request data
      Object.keys(requestData).forEach(key => {
        if (key !== 'attachments') {
          formData.append(key, requestData[key]);
        }
      });

      // Add attachments if any
      if (requestData.attachments) {
        requestData.attachments.forEach(file => {
          formData.append('attachments', file);
        });
      }

      const response = await api.put(`/request-system/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  processRequestApproval: async (id, approvalData) => {
    try {
      const response = await api.put(`/request-system/${id}/process`, approvalData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Request Statistics
  getRequestStatistics: async () => {
    try {
      const response = await api.get('/request-system/statistics');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Request Templates
  getRequestTemplates: async () => {
    try {
      const response = await api.get('/request-system/templates');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getRequestTemplate: async (id) => {
    try {
      const response = await api.get(`/request-system/templates/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createRequestTemplate: async (templateData) => {
    try {
      const response = await api.post('/request-system/templates', templateData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateRequestTemplate: async (id, templateData) => {
    try {
      const response = await api.put(`/request-system/templates/${id}`, templateData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteRequestTemplate: async (id) => {
    try {
      const response = await api.delete(`/request-system/templates/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Request Categories
  getRequestCategories: async () => {
    try {
      const response = await api.get('/request-system/categories');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getRequestCategory: async (id) => {
    try {
      const response = await api.get(`/request-system/categories/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createRequestCategory: async (categoryData) => {
    try {
      const response = await api.post('/request-system/categories', categoryData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateRequestCategory: async (id, categoryData) => {
    try {
      const response = await api.put(`/request-system/categories/${id}`, categoryData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteRequestCategory: async (id) => {
    try {
      const response = await api.delete(`/request-system/categories/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // File Upload Helper
  uploadFile: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Download Attachment
  downloadAttachment: async (requestId, attachmentId) => {
    try {
      const response = await api.get(`/request-system/${requestId}/attachments/${attachmentId}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete Attachment
  deleteAttachment: async (requestId, attachmentId) => {
    try {
      const response = await api.delete(`/request-system/${requestId}/attachments/${attachmentId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default requestService; 